import getTournamentMyScore from "@casino/actions/Games/getTournamentMyScore";
import optInToTournament from "@casino/actions/Games/optInToTournament";
import { useGamePageData } from "@casino/ui/Games/hooks";
import type { TournamentFragment } from "@workspace/casino_core/fragments/TournamentFragment";
import { useEffect, useState } from "react";

type Args = {
  tournamentId: string;
  currency?: string;
  isOptedIn: boolean;
};

const POLL_INTERVAL_MS = 30_000;

const useTournamentOptIn = ({ tournamentId, currency, isOptedIn }: Args) => {
  const { isLoggedIn, requestAuth } = useGamePageData();
  const [isOn, setIsOn] = useState(isOptedIn);
  const [isPending, setIsPending] = useState(false);
  const [freshMyScore, setFreshMyScore] = useState<
    TournamentFragment["myScore"] | undefined
  >(undefined);

  useEffect(() => {
    if (!isLoggedIn || !isOn) return;

    let isCancelled = false;
    const poll = async () => {
      const myScore = await getTournamentMyScore({ tournamentId });
      if (isCancelled || !myScore) return;
      setFreshMyScore(myScore);
    };

    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [isLoggedIn, isOn, tournamentId]);

  const handleOptIn = async () => {
    if (isPending || isOn) return;

    if (!isLoggedIn) {
      requestAuth();
      return;
    }

    setIsPending(true);
    const result = await optInToTournament({ tournamentId, currency });
    setIsPending(false);

    if (result.errors.length) return;

    setIsOn(result.isOptedIn);
    setFreshMyScore(result.myScore);
  };

  return { isOptedIn: isOn, isPending, handleOptIn, myScore: freshMyScore };
};

export default useTournamentOptIn;
